import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AnimatePresence, LayoutGroup, motion } from 'framer-motion';
import { toast } from 'sonner';
import { cn } from '@/utils/cn';
import { useAuth } from '@/contexts/AuthContext';
import { useCountry } from '@/contexts/CountryContext';
import { Select } from '@/components/ui';
import { navigationByRole, type NavItem, type RoleNavigation } from '@/config/navigation';
import { usePendingLeads } from '@/contexts/PendingLeadsContext';
import { currencyService } from '@/services/currency.service';
import { languageService } from '@/services/language.service';
import { CountryModal } from '@/pages/countries/CountryModal';
import { backdropFade, spring } from '@/lib/motion';
import type { StaffRole } from '@/types';
import type { Currency } from '@/types/currency.types';
import type { Language } from '@/types/language.types';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const ACTIVE_PILL_ID = 'sidebar-active-pill';

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const location = useLocation();
  const { user } = useAuth();
  const { pendientes } = usePendingLeads();
  const {
    countries,
    isLoadingCountries,
    selectedCountryId,
    setSelectedCountryId,
    selectedCountry,
    refreshCountries,
    isSuperAdmin,
  } = useCountry();

  const userRole = user?.profiles?.staff?.role as StaffRole | undefined;
  const navigation: RoleNavigation = userRole
    ? navigationByRole[userRole]
    : { global: [], country: [] };

  const allNavItems = [...navigation.global, ...navigation.country].filter(
    (item) => !item.section && !!item.href,
  );

  const isItemActive = (item: NavItem) => {
    const itemHref = item.href;
    if (!itemHref) return false;
    const path = location.pathname;
    const isMatch = path === itemHref || path.startsWith(itemHref + '/');
    if (!isMatch) return false;

    // Buscar si existe algún otro item en la navegación que también coincida pero con un href más largo
    const hasBetterMatch = allNavItems.some(
      (other) =>
        other.href !== itemHref &&
        other.href &&
        (path === other.href || path.startsWith(other.href + '/')) &&
        other.href.length > itemHref.length,
    );

    return !hasBetterMatch;
  };

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [lookupsLoaded, setLookupsLoaded] = useState(false);

  // Secciones del nav colapsadas (por título). El nav de SUPER_ADMIN supera
  // los 30 ítems — poder plegar grupos lo mantiene escaneable a medida que
  // el negocio crece. Persistimos la preferencia por usuario en localStorage.
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(
    () => {
      try {
        const raw = localStorage.getItem('admin-nav-collapsed');
        return new Set(raw ? (JSON.parse(raw) as string[]) : []);
      } catch {
        return new Set();
      }
    },
  );

  const toggleSection = (title: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(title)) next.delete(title);
      else next.add(title);
      try {
        localStorage.setItem('admin-nav-collapsed', JSON.stringify([...next]));
      } catch {
        // localStorage lleno/bloqueado — la preferencia solo vive en memoria.
      }
      return next;
    });
  };

  const fetchLookups = async () => {
    if (lookupsLoaded) return;
    try {
      const [currRes, langRes] = await Promise.all([
        currencyService.getAll({ limit: 100 }),
        languageService.getAll({ limit: 100 }),
      ]);
      setCurrencies(currRes.data);
      setLanguages(langRes.data);
      setLookupsLoaded(true);
    } catch {
      toast.error('Error al cargar monedas e idiomas');
    }
  };

  const handleOpenCreateCountry = async () => {
    await fetchLookups();
    setIsCreateModalOpen(true);
  };

  const handleCreateCountrySuccess = async () => {
    setIsCreateModalOpen(false);
    await refreshCountries();
  };

  const renderNavSections = (items: NavItem[]) => {
    const sections: { title?: string; items: NavItem[] }[] = [];
    let currentSection: { title?: string; items: NavItem[] } = { items: [] };

    items.forEach((item) => {
      if (item.section) {
        if (currentSection.items.length > 0 || currentSection.title) {
          sections.push(currentSection);
        }
        currentSection = { title: item.label, items: [] };
      } else {
        currentSection.items.push(item);
      }
    });

    if (currentSection.items.length > 0 || currentSection.title) {
      sections.push(currentSection);
    }

    return sections.map((section, sectionIndex) => {
      // La sección que contiene la ruta activa NUNCA se oculta — colapsarla
      // dejaría al usuario sin saber dónde está.
      const hasActiveItem = section.items.some(
        (item) => isItemActive(item),
      );
      const isCollapsed =
        !!section.title &&
        collapsedSections.has(section.title) &&
        !hasActiveItem;

      const itemsList = (
        <ul className="space-y-0.5">
          {section.items.map((item) => {
            const isActive = isItemActive(item);
            return (
              <li key={item.href}>
                <Link
                  to={item.href!}
                  // Ancla de las guías interactivas, derivada del href: una
                  // entrada nueva del menú trae la suya sin tocar nada.
                  // `/content-creators` → `admin.nav.content-creators`.
                  // Ver `src/tour/`.
                  data-tour={`admin.nav${item.href!.replace(/\//g, '.')}`}
                  onClick={onClose}
                  className={cn(
                    'group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium',
                    'transition-colors duration-200 ease-[var(--ease-standard)]',
                    isActive
                      ? 'text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {isActive && (
                    <motion.span
                      layoutId={ACTIVE_PILL_ID}
                      className="absolute inset-0 rounded-lg bg-primary"
                      transition={spring.responsive}
                      style={{ zIndex: 0 }}
                    />
                  )}
                  {!isActive && (
                    <span
                      aria-hidden="true"
                      className="absolute inset-0 rounded-lg bg-interactive-bg opacity-0 transition-opacity duration-200 group-hover:opacity-100"
                      style={{ zIndex: 0 }}
                    />
                  )}
                  <span className="relative z-[1] flex h-5 w-5 items-center justify-center shrink-0">
                    {item.icon}
                  </span>
                  <span className="relative z-[1] truncate">{item.label}</span>
                  {/* Cuántas esperan sin trabajar. Sólo en las entradas que
                      son una cola —solicitudes—, y sólo si hay algo: un cero
                      permanente se vuelve parte del decorado y deja de
                      leerse. */}
                  {pendientes[item.href!] > 0 && (
                    <span
                      className={cn(
                        'relative z-[1] ml-auto flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full px-1.5 text-[11px] font-semibold tabular-nums',
                        isActive
                          ? 'bg-primary-foreground/20 text-primary-foreground'
                          : 'bg-primary text-primary-foreground',
                      )}
                      aria-label={`${pendientes[item.href!]} sin trabajar`}
                    >
                      {pendientes[item.href!] > 99
                        ? '99+'
                        : pendientes[item.href!]}
                    </span>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      );

      return (
        <div key={section.title ?? sectionIndex}>
          {section.title ? (
            <button
              type="button"
              onClick={() => toggleSection(section.title!)}
              aria-expanded={!isCollapsed}
              className="group/section flex w-full cursor-pointer items-center justify-between rounded-md px-3 mb-2 py-0.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-[0.12em] transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <span>{section.title}</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
                className={cn(
                  'h-3.5 w-3.5 shrink-0 opacity-0 transition-all duration-200 group-hover/section:opacity-100',
                  isCollapsed ? '-rotate-90 opacity-60' : 'rotate-0',
                )}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
          ) : null}
          <AnimatePresence initial={false}>
            {!isCollapsed && (
              <motion.div
                key="items"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.18, ease: 'easeInOut' }}
                className="overflow-hidden"
              >
                {itemsList}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      );
    });
  };

  const SidebarContent = (
    <>
      <div className="flex h-16 items-center justify-between border-b border-border px-6">
        <Link to="/" className="flex items-center gap-2.5 group">
          <motion.div
            whileHover={{ rotate: -4 }}
            transition={spring.snappy}
            className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground font-bold text-[11px]"
          >
            121
          </motion.div>
          <span className="font-display text-[17px] tracking-tight text-foreground">
            PASS Admin
          </span>
        </Link>
        <motion.button
          onClick={onClose}
          whileTap={{ scale: 0.9 }}
          className="cursor-pointer rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:hidden"
          aria-label="Cerrar menú"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </motion.button>
      </div>

      <nav className="flex-1 overflow-y-auto p-4">
        {navigation.global.length > 0 ? (
          <div className="space-y-6" data-tour="admin.sidebar.global">
            {renderNavSections(navigation.global)}
          </div>
        ) : navigation.country.length === 0 ? (
          <p className="text-sm text-muted-foreground px-3 py-2">Sin acceso a módulos</p>
        ) : null}

        {navigation.country.length > 0 && (
          <div
            data-tour="admin.sidebar.countrySection"
            className={
              navigation.global.length > 0 ? 'mt-6 border-t border-border pt-5' : ''
            }
          >
            <h3 className="px-3 mb-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-[0.12em]">
              Operación por País
            </h3>

            {isSuperAdmin ? (
              <div
                className="px-2 mb-3 flex items-center gap-1.5"
                data-tour="admin.sidebar.countrySelector"
              >
                <Select
                  value={selectedCountryId}
                  onChange={(e) => setSelectedCountryId(e.target.value)}
                  disabled={isLoadingCountries}
                  placeholder={isLoadingCountries ? 'Cargando...' : 'Seleccionar país'}
                  options={countries.map((country) => ({
                    value: country.id,
                    label: `${country.flagEmoji ? `${country.flagEmoji} ` : ''}${country.name}`,
                  }))}
                  className="font-medium"
                />
                <motion.button
                  onClick={handleOpenCreateCountry}
                  whileTap={{ scale: 0.92 }}
                  transition={spring.snappy}
                  title="Nuevo país"
                  aria-label="Nuevo país"
                  className="cursor-pointer flex-shrink-0 flex h-[38px] w-[38px] items-center justify-center rounded-lg border border-dashed border-border text-muted-foreground transition-colors duration-200 hover:border-primary hover:text-primary hover:bg-primary/5"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                </motion.button>
              </div>
            ) : (
              <div className="px-2 mb-3">
                <div className="rounded-lg bg-primary/5 border border-primary/20 px-3 py-2 text-sm font-medium text-primary">
                  {selectedCountry
                    ? `${selectedCountry.flagEmoji ? `${selectedCountry.flagEmoji} ` : ''}${selectedCountry.name}`
                    : user?.profiles?.staff?.countryName || 'País asignado'}
                </div>
              </div>
            )}

            {selectedCountryId ? (
              <div className="space-y-6">{renderNavSections(navigation.country)}</div>
            ) : isSuperAdmin ? (
              <motion.div
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
                className="mx-2 rounded-lg bg-muted/50 px-3 py-3"
              >
                <p className="text-xs text-muted-foreground text-center">
                  Selecciona un país para ver sus opciones
                </p>
              </motion.div>
            ) : null}
          </div>
        )}
      </nav>

      <div className="border-t border-border p-4">
        <p className="text-[11px] text-muted-foreground">
          © 2026 121PASS. Todos los derechos reservados.
        </p>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop sidebar — always visible from lg breakpoint */}
      <LayoutGroup id="sidebar-desktop">
        <aside className="hidden lg:flex lg:static lg:h-full lg:w-64 lg:flex-col lg:border-r lg:border-border lg:bg-background">
          {SidebarContent}
        </aside>
      </LayoutGroup>

      {/* Mobile overlay + sliding sidebar */}
      <AnimatePresence>
        {isOpen && (
          <LayoutGroup id="sidebar-mobile">
            <div className="lg:hidden">
              <motion.div
                key="sidebar-backdrop"
                variants={backdropFade}
                initial="hidden"
                animate="visible"
                exit="exit"
                onClick={onClose}
                className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
                aria-hidden="true"
              />
              <motion.aside
                key="sidebar-panel"
                initial={{ x: '-100%' }}
                animate={{ x: 0, transition: spring.soft }}
                exit={{ x: '-100%', transition: { duration: 0.3 } }}
                className="fixed left-0 top-0 z-50 flex h-full w-64 flex-col border-r border-border bg-background"
                style={{ boxShadow: 'var(--shadow-panel)' }}
              >
                {SidebarContent}
              </motion.aside>
            </div>
          </LayoutGroup>
        )}
      </AnimatePresence>

      <CountryModal
        isOpen={isCreateModalOpen}
        country={null}
        currencies={currencies}
        languages={languages}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handleCreateCountrySuccess}
      />
    </>
  );
}

export default Sidebar;
